"use client"
import type { Swiper as SwiperType } from "swiper"
import SwiperCore, { Lazy, Navigation, Pagination, Thumbs, Zoom } from "swiper"
import { Swiper } from "swiper/react"
import { clsx } from "clsx"

import { ChevronLeftIcon, ChevronRightIcon } from "@shared/ui.icons"
import { trackProductImageViewed } from "utils/gtm"
import { Logger } from "@shared/utils.logger"
import { useRef, useState, useEffect } from "react"
import { withErrorBoundary } from "react-error-boundary"
import { GalleryImageSlide } from "./GalleryImageSlide/GalleryImageSlide"
import { GalleryVideoSlide } from "./GalleryVideoSlide/GalleryVideoSlide"
import { GalleryThumbnails } from "./GalleryThumbnails/GalleryThumbnails"
import { useMediaQueries } from "@shared/ui.hooks"
import { dispatch } from "@shared/utils.events"

import * as styles from "./Gallery.css"

import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/thumbs"
import "swiper/css/zoom"
import { GalleryZoom } from "./GalleryZoom/GalleryZoom"
import type { Gallery as GalleryType } from "@shared/graphql.types"
import { isVideoType } from "./utils/utils"
import type { PDPImage } from "@shared/graphql.types"
import { Badge } from "./Badge/Badge"

SwiperCore.use([Lazy, Navigation, Zoom, Thumbs, Pagination])

type GalleryProps = {
  gallery: GalleryType
  useCloudinary: boolean
  badge?: PDPImage
}

export const GalleryComponent: React.FC<GalleryProps> = ({
  gallery,
  useCloudinary = false,
  badge,
}) => {
  const { media } = gallery
  const [zoomIndex, setZoomIndex] = useState(-1)
  const container = useRef<HTMLDivElement>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null)
  const [initialIndex, setInitialIndex] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)
  const { isMd } = useMediaQueries()

  const hideZoomMode = () => {
    setThumbsSwiper(null)
    setIsZoomed(false)
    setZoomIndex(-1)
    dispatch("page element clicked", {
      target: "PDP Gallery Zoom",
      details: "Image zoom closed",
      location: "PDP Gallery Zoom",
    })
  }

  const onZoomChange = (_swiper, scale) => {
    setIsZoomed(scale !== 1)
    const zoomDirection = scale > 1 ? "in" : "out"
    dispatch("page element clicked", {
      target: "PDP Gallery Zoom",
      details: `Image zoomed ${zoomDirection}`,
      location: "PDP Gallery Zoom",
      position: zoomIndex,
    })
  }

  const thumbsSize = isMd ? 80 : 96

  useEffect(() => {
    if (!isMd) {
      thumbsSwiper?.changeDirection("vertical")
    } else {
      thumbsSwiper?.changeDirection("horizontal")
    }
  }, [isMd, thumbsSwiper])

  useEffect(() => {
    if (thumbsSwiper) {
      thumbsSwiper.on("transitionEnd", () => {
        dispatch("page element clicked", {
          target: "PDP Gallery Thumbnails",
          details: `Thumbnail dragged`,
          location: "PDP Gallery Thumbnails",
          position: thumbsSwiper.clickedIndex,
        })
      })
    }
    return () => {
      thumbsSwiper?.off("transitionEnd")
    }
  }, [thumbsSwiper])
  useEffect(() => {
    if (mainSwiper) {
      mainSwiper.on("sliderFirstMove", () => {
        dispatch("page element clicked", {
          target: "PDP Gallery",
          details: "Image dragged",
          location: "PDP Gallery",
          position: mainSwiper.clickedIndex,
        })
      })
    }
    return () => {
      mainSwiper?.off("sliderFirstMove")
    }
  }, [mainSwiper])
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (zoomIndex > -1) hideZoomMode()
      }
    }

    if (zoomIndex > -1) {
      window.addEventListener("keydown", onKeyDown)
    } else {
      window.removeEventListener("keydown", onKeyDown)
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomIndex])

  return (
    <div className={styles.galleryContainer}>
      <div className={styles.aspectRatioContainer}>
        <div
          id="PDPGallery"
          className={`${styles.swiperBox} ${
            zoomIndex > -1 ? styles.swiperBoxZoomed : ""
          }`}
          ref={container}
        >
          {zoomIndex === -1 && (
            <div className={styles.mainSwiper}>
              <Swiper
                onInit={(swiper) => {
                  if (
                    typeof swiper.params.navigation !== "boolean" &&
                    swiper.params.navigation
                  ) {
                    const { navigation } = swiper.params
                    navigation.prevEl = ".new-swiper-button-prev"
                    navigation.nextEl = ".new-swiper-button-next"
                  }
                  swiper.navigation.update()
                  setMainSwiper(swiper)
                }}
                navigation={{
                  nextEl: ".new-swiper-button-next",
                  prevEl: ".new-swiper-button-prev",
                }}
                rewind={true}
                pagination
                zoom
                thumbs={{ swiper: thumbsSwiper }}
                initialSlide={initialIndex}
                centeredSlides
                centeredSlidesBounds
                watchSlidesProgress
                slidesPerView="auto"
                spaceBetween={6}
                onClick={(swiper, event) => {
                  const { className } = event.target as HTMLElement
                  if (className.startsWith("vjs-")) return
                  if (
                    !["swiper-button-next", "swiper-button-prev"].includes(
                      className
                    )
                  ) {
                    setZoomIndex(swiper.clickedIndex)
                    trackProductImageViewed()
                    dispatch("page element clicked", {
                      target: "PDP Gallery",
                      details: `Image expanded - ${swiper.clickedIndex}`,
                      location: "PDP Gallery",
                    })
                  }
                }}
                noSwipingClass="vjs-control-bar"
                onSlideChange={(swiper) => {
                  dispatch("swiper.onslidechange", {
                    target: "PDP Gallery",
                    position: swiper?.realIndex,
                  })
                  setInitialIndex(swiper.realIndex)
                  // Force the thumbsSwiper to mark the correct slide as active
                  // without changing its scroll position
                  if (thumbsSwiper) {
                    const currentActiveIndex = thumbsSwiper.activeIndex
                    const targetActiveIndex = swiper.activeIndex
                    if (currentActiveIndex !== targetActiveIndex) {
                      // This updates the internal active index without scrolling
                      thumbsSwiper.activeIndex = targetActiveIndex
                      thumbsSwiper.updateSlidesClasses()
                    }
                  }
                }}
              >
                {badge && <Badge badge={badge} />}
                {media?.map((item, index) => {
                  if (isVideoType(item)) {
                    return (
                      <GalleryVideoSlide
                        key={`full-video-player-${index}`}
                        item={item}
                        index={index}
                      />
                    )
                  }
                  return (
                    <GalleryImageSlide
                      key={`full-${index}`}
                      item={item}
                      index={index}
                      useCloudinary={useCloudinary}
                    />
                  )
                })}

                <div className={styles.swiperButtonContainer}>
                  <button
                    id="button-test"
                    onClick={() => {
                      trackProductImageViewed()
                      dispatch("page element clicked", {
                        target: "PDP Gallery",
                        details: "Image navigated - previous",
                        location: "PDP Gallery",
                      })
                    }}
                    className={`new-swiper-button-prev ${styles.swiperNavButton}`}
                  >
                    <ChevronLeftIcon height={20} width={20} />
                  </button>
                  <button
                    id="button-test-2"
                    onClick={() => {
                      trackProductImageViewed()
                      dispatch("page element clicked", {
                        target: "PDP Gallery",
                        details: "Image navigated - next",
                        location: "PDP Gallery",
                      })
                    }}
                    className={`new-swiper-button-next ${styles.swiperNavButton}`}
                  >
                    <ChevronRightIcon height={20} width={20} />
                  </button>
                </div>
              </Swiper>
            </div>
          )}

          {zoomIndex > -1 && (
            <GalleryZoom
              onZoomChange={onZoomChange}
              zoomIndex={zoomIndex}
              media={media}
              thumbsSize={thumbsSize}
              isZoomed={isZoomed}
              hideZoomMode={hideZoomMode}
              useCloudinary={useCloudinary}
            />
          )}
        </div>
      </div>

      {zoomIndex === -1 && (
        <div
          id="side-container"
          className={clsx(
            styles.thumbnailContainer,
            // @todo: Check if we can remove this and find a CSS solution
            // Hide for desktop until thumbsSwiper is initialised to prevent layout shift
            thumbsSwiper ? styles.thumbnailContainerActive : ""
          )}
        >
          <GalleryThumbnails
            media={media}
            useCloudinary={useCloudinary}
            setThumbsSwiper={setThumbsSwiper}
            initialIndex={initialIndex}
            handleThumbnailClick={(thumbnailId: string, type: string) => {
              dispatch("page element clicked", {
                target: "PDP - Gallery Thumbnails",
                details: `Thumbnail Clicked - ${type}`,
                location: "PDP - Gallery",
                locationDetails: thumbnailId,
              })
            }}
          />
        </div>
      )}
    </div>
  )
}

export const Gallery = withErrorBoundary(GalleryComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`ProductGallery failed to load. ${error}`)
  },
})
